import { Component, OnInit, Signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  MsalService,
  MsalModule,
  MsalBroadcastService
} from '@azure/msal-angular';
import {
  InteractionStatus,
  EventMessage,
  EventType,
  AuthenticationResult,
  BrowserAuthError,
} from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from './common/services/theme.service';
import { HeaderComponent } from "./common/components/header/header.component";
import { FooterComponent } from "./common/components/footer/footer.component";
import { LeftSidebarComponent } from "./common/components/left-sidebar/left-sidebar.component";
import { UserService } from './common/services/user.service';
import { DestroyRef } from '@angular/core';
import { BlockUIModule } from 'primeng/blockui';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { LoadingService } from './common/services/loading.service';
import { InsightsService } from './common/services/insights.service';
import { HelpOverlayComponent } from "./common/components/help-overlay/help-overlay.component";
import { HelpDisplayService } from './common/services/help-display.service';
import { DowntimeService } from './common/services/downtime.service';
import { NavigationService } from './common/services/navigation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    CommonModule,
    MsalModule,
    BlockUIModule,
    ProgressSpinnerModule,
    PanelModule,
    ToastModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    LeftSidebarComponent,
    HelpOverlayComponent
  ],
})
export class AppComponent implements OnInit {
  isIframe = false;
  loading: Signal<boolean>;
  authenticated: Signal<boolean>;
  showHelp: Signal<boolean>;
  isLogoutPage = false;
  pageTitle: Signal<string>;
  private destroyRef = inject(DestroyRef);

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private theme: ThemeService,
    private user: UserService,
    private loader: LoadingService,
    private insights: InsightsService,
    private help: HelpDisplayService,
    private router: Router,
    private downtime: DowntimeService,
    private navService: NavigationService
  ) {
    this.authenticated = this.user.authenticated;
    this.loading = this.loader.loading;
    this.showHelp = this.help.visible;
    this.pageTitle = this.navService.pageTitle;
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(e => {
      this.isLogoutPage = ['/logged-out', '/not-authorized'].includes(e.url);
      this.isLogoutPage && this.loader.loading.set(false);
    });
  }

  ngOnInit(): void {
    this.theme.setDarkOrLightTheme('initial');
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/logged-out';
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE)
      )
      .subscribe((result: EventMessage) => {
        if (result.error instanceof BrowserAuthError && result.error.errorCode === 'popup_window_error') {
          this.loader.popupBlocked.set(true);
        }
        
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
    if (activeAccount) {
      this.insights.trackTrace(`${activeAccount.username} was authenticated`);
      this.user.setUserInfo(activeAccount);
      this.downtime.getDowntime();
    }
  }
}